"use client"

import { ChevronLeft } from "lucide-react"
import { TurnstileWidget } from "@/components/TurnstileWidget"
import { NoSlotButton } from "@/components/rdv/NoSlotButton"
import { FONT_HEADING, FONT_MONO, FONT_SANS } from "@/lib/constants"
import type { RdvFlow } from "../_hooks/useRdvFlow"
import { ResidenceDayBlock } from "./ResidenceDayBlock"

export function Step3Creneaux({ flow }: { flow: RdvFlow }) {
  const {
    needsReviewMode,
    sessionToken,
    slots,
    slotsByDay,
    slotDuration,
    selectedSlot,
    setSelectedSlot,
    loadingSlots,
    loadingSlotsForDate,
    submitting,
    submitError,
    turnstileToken,
    setTurnstileToken,
    residenceDays,
    residenceDayFilter,
    showResidenceDayBlock,
    outOfZone,
    geocodingFailed,
    selectResidenceDay,
    showAllSlots,
    backToStep2,
    submit,
    setNoSlotResult,
    buildNoSlotPayload,
  } = flow

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <button
        onClick={backToStep2}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "#0EA5E9",
          fontFamily: FONT_SANS,
          fontSize: 14,
          fontWeight: 600,
          padding: 0,
          marginBottom: 4,
        }}
      >
        <ChevronLeft size={16} /> Retour
      </button>

      {/* Bannière vérification en cours */}
      {needsReviewMode && (
        <div
          style={{
            background: "#FEF3C7",
            border: "1px solid #FDE68A",
            borderRadius: 12,
            padding: "14px 18px",
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
          }}
        >
          <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
          <div>
            <p
              style={{
                margin: 0,
                fontWeight: 700,
                fontSize: 13,
                color: "#92400E",
                fontFamily: FONT_SANS,
              }}
            >
              Votre propriétaire n&apos;a pas été reconnu
            </p>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 12,
                color: "#78350F",
                lineHeight: 1.5,
                fontFamily: FONT_SANS,
              }}
            >
              Votre demande sera soumise à vérification avant d&apos;être confirmée. Sélectionnez un
              créneau pour soumettre la demande.
            </p>
          </div>
        </div>
      )}

      {/* Bloc journée résidence */}
      {showResidenceDayBlock && residenceDays.length > 0 && (
        <ResidenceDayBlock
          residenceDays={residenceDays}
          onSelectDay={selectResidenceDay}
          onShowAllSlots={showAllSlots}
        />
      )}

      {/* Créneaux */}
      {!showResidenceDayBlock && (
        <div
          style={{
            background: "var(--ljs-surface)",
            border: "1px solid var(--ljs-border)",
            borderRadius: 16,
            padding: 24,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <h2
              style={{
                fontFamily: FONT_HEADING,
                fontSize: 20,
                fontWeight: 700,
                color: "var(--ljs-text)",
                margin: 0,
              }}
            >
              Choisissez un créneau
            </h2>
            {residenceDayFilter && (
              <button
                onClick={showAllSlots}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#0EA5E9",
                  fontFamily: FONT_SANS,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                Voir toutes les dates
              </button>
            )}
          </div>
          <p style={{ fontSize: 13, color: "var(--ljs-text-faint)", margin: "0 0 20px" }}>
            {residenceDayFilter
              ? "Créneaux disponibles sur la journée résidence"
              : `Durée estimée : ${slotDuration} minutes`}
          </p>

          {loadingSlots || loadingSlotsForDate ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 180,
                gap: 12,
                color: "var(--ljs-text-faint)",
                fontSize: 13,
              }}
            >
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  border: "2.5px solid #0EA5E9",
                  borderTopColor: "transparent",
                  animation: "spin 0.7s linear infinite",
                }}
              />
              Chargement des disponibilités…
            </div>
          ) : Object.keys(slotsByDay).length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px 0",
                color: "var(--ljs-text-faint)",
                fontSize: 14,
                lineHeight: 1.7,
              }}
            >
              {residenceDayFilter ? (
                "Aucun créneau disponible sur cette journée. Essayez une autre date."
              ) : outOfZone ? (
                <>
                  <span style={{ fontSize: 24, display: "block", marginBottom: 8 }}>📍</span>
                  Votre adresse se situe en dehors de notre zone d&apos;intervention.
                  <br />
                  <span style={{ fontSize: 13 }}>
                    Contactez-nous directement pour vérifier nos possibilités.
                  </span>
                </>
              ) : geocodingFailed ? (
                <>
                  <span style={{ fontSize: 24, display: "block", marginBottom: 8 }}>🔍</span>
                  Adresse introuvable — les créneaux sont affichés sans contrainte de trajet.
                  <br />
                  <span style={{ fontSize: 13 }}>
                    Appelez-nous si aucun créneau ne correspond à vos besoins.
                  </span>
                </>
              ) : (
                <>
                  Aucun créneau disponible dans les prochains jours.
                  <br />
                  <span style={{ fontSize: 13 }}>Appelez-nous directement.</span>
                </>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {Object.entries(slotsByDay).map(([day, daySlots]) => {
                const dayLabel = daySlots[0].label.split(" à ")[0]
                return (
                  <div key={day}>
                    <p
                      style={{
                        fontFamily: FONT_SANS,
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#475569",
                        textTransform: "capitalize",
                        margin: "0 0 8px",
                      }}
                    >
                      {dayLabel}
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {daySlots.map((slot) => {
                        const isSelected = selectedSlot === slot.date
                        return (
                          <button
                            key={slot.date}
                            onClick={() => setSelectedSlot(slot.date)}
                            style={{
                              padding: "8px 16px",
                              borderRadius: 8,
                              border: isSelected
                                ? "2px solid #0EA5E9"
                                : "1px solid var(--ljs-border)",
                              background: isSelected
                                ? "rgba(14,165,233,0.08)"
                                : "var(--ljs-surface)",
                              color: isSelected ? "#0EA5E9" : "var(--ljs-text-2)",
                              fontFamily: FONT_MONO,
                              fontSize: 13,
                              fontWeight: isSelected ? 700 : 500,
                              cursor: "pointer",
                              transition: "all 0.12s",
                            }}
                          >
                            {slot.time}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Vérification anti-bot (Cloudflare Turnstile) — ignorée si session vérifiée */}
      {selectedSlot && !turnstileToken && !sessionToken && (
        <div style={{ display: "flex", justifyContent: "center", margin: "8px 0" }}>
          <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />
        </div>
      )}

      {/* Erreur de soumission */}
      {submitError && (
        <div
          style={{
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            borderRadius: 10,
            padding: "12px 16px",
            fontSize: 13,
            color: "#DC2626",
            fontFamily: FONT_SANS,
          }}
        >
          {submitError}
        </div>
      )}

      {/* Créneau sélectionné */}
      {selectedSlot && (
        <div
          style={{
            background: "rgba(14,165,233,0.06)",
            border: "1px solid rgba(14,165,233,0.2)",
            borderRadius: 12,
            padding: "14px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <p
              style={{
                fontFamily: FONT_SANS,
                fontSize: 12,
                color: "#0EA5E9",
                fontWeight: 600,
                margin: "0 0 2px",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Créneau sélectionné
            </p>
            <p
              style={{
                fontFamily: FONT_SANS,
                fontSize: 14,
                fontWeight: 600,
                color: "var(--ljs-text)",
                margin: 0,
                textTransform: "capitalize",
              }}
            >
              {slots.find((s) => s.date === selectedSlot)?.label}
            </p>
          </div>
          <button
            onClick={submit}
            disabled={submitting || (!turnstileToken && !sessionToken)}
            style={{
              background: "#0EA5E9",
              color: "#fff",
              fontFamily: FONT_SANS,
              fontSize: 14,
              fontWeight: 700,
              padding: "11px 22px",
              borderRadius: 8,
              border: "none",
              cursor: submitting || (!turnstileToken && !sessionToken) ? "not-allowed" : "pointer",
              opacity: submitting || (!turnstileToken && !sessionToken) ? 0.7 : 1,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {submitting ? "Confirmation…" : "Confirmer le RDV →"}
          </button>
        </div>
      )}

      {/* Sortie alternative — aucun créneau ne convient (séparé de la liste) */}
      {!showResidenceDayBlock && !loadingSlots && !loadingSlotsForDate && (
        <div
          style={{
            borderTop: "1px solid var(--ljs-border)",
            paddingTop: 16,
            marginTop: 4,
          }}
        >
          <p
            style={{
              fontFamily: FONT_SANS,
              fontSize: 12,
              color: "var(--ljs-text-faint)",
              margin: "0 0 10px",
            }}
          >
            Aucune de ces disponibilités ne vous convient ?
          </p>
          <NoSlotButton
            payload={buildNoSlotPayload()}
            onDone={(result) => setNoSlotResult(result)}
            disabled={submitting}
          />
        </div>
      )}
    </div>
  )
}
